import { useState, useRef, useEffect } from 'react'
import {
  Card,
  CardContent,
  makeStyles,
  InputBase,
  Divider,
  Snackbar,
  useMediaQuery,
  Link as MuiLink,
} from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'
import ButtonSp from '../ui/ButtonSp'
import TrackingHelpModal from '../tracking/TrackingHelpModal'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import { couriersCodeToNameList } from '../utility/courier'
import {
  orderStatusToTextMap,
  orderStatusToTextMapEng,
} from '../utility/trackingUtility'
import theme from '../common/theme'
import SignatureModal from './SignatureModal'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Axios from 'axios'
import Link from '../utility/CustomLink'

const CopiedSnackBar = ({ open, setOpen, text }) => {
  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      open={open}
      autoHideDuration={1000}
      onClose={() => setOpen(false)}
      message={text}
    />
  )
}

const CopyField = ({ text, copiedText }) => {
  const [copied, setCopied] = useState(false)
  const copyFieldRef = useRef(null)
  const copyToClipboard = (e) => {
    if (typeof window !== 'undefined') {
      copyFieldRef.current.select()
      document.execCommand('copy')
      e.target.focus()
      setCopied(true)
    }
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
        <div
          style={{
            width: '100%',
            backgroundColor: 'white',
            padding: '5px 10px',
            textAlign: 'left',
            borderTopLeftRadius: 10,
            borderBottomLeftRadius: 10,
            border: '1px solid #CCCCCC',
            borderRight: 0,
          }}
        >
          <InputBase fullWidth value={text} inputRef={copyFieldRef} readOnly />
        </div>
        <ButtonSp
          style={{
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderTopRightRadius: 10,
            borderBottomRightRadius: 10,
            width: 64,
          }}
          variant="outlined"
          onClick={copyToClipboard}
        >
          <FileCopyOutlinedIcon fontSize="small" />
        </ButtonSp>
      </div>
      <CopiedSnackBar open={copied} setOpen={setCopied} text={copiedText} />
    </>
  )
}

const DetailCard = ({ data }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const { t } = useTranslation('tracking')
  const Router = useRouter()
  const language = Router.locale

  const [isHelpModalOpen, setHelpModalOpen] = useState(false)
  const [isSnackbarOpen, setSnackbarOpen] = useState(false)
  const [isSignatureModalOpen, setSignatureModalOpen] = useState(false)
  const [courierLogoSrc, setCourierLogoSrc] = useState(false)
  const mobileUrlRef = useRef(null)

  useEffect(() => {
    const fetchCourierLogo = async () => {
      try {
        if (data.courier_logo) {
          setCourierLogoSrc(data.courier_logo)
        } else {
          const {
            data: { image: fetchedCourierLogoSrc },
          } = await Axios.post('/api/get-single-courier-logo', {
            courier_code: data['courier_code'],
          })
          setCourierLogoSrc(fetchedCourierLogoSrc.image)
        }
      } catch (err) {
        // do nothing
      }
    }
    fetchCourierLogo()
  }, [data])

  const useStyle = makeStyles({
    row: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      marginBottom: 20,
    },
    title: {
      fontWeight: 400,
      fontSize: 16,
      color: '#BDBDBD',
      paddingRight: 10,
    },
    detail: {
      fontWeight: 400,
      fontSize: 16,
      color: '#404041',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    fullWidth: {
      width: '100%',
    },
    header: {
      fontWeight: 400,
      fontSize: 22,
      margin: 8,
      ...theme.headerFont,
      color: '#404041',
    },
    divider: {
      margin: '10px -16px 30px -16px',
    },
    icon: {
      marginLeft: 5,
    },
    card: {
      ...theme.homeStyle.boxShadow,
    },
    signature: {
      width: 200,
      height: 70,
      objectFit: 'contain',
    },
    signatureArea: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
    },
    seeSignature: {
      marginTop: theme.spacing(1),
      cursor: 'pointer',
    },
    bannerImage: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      cursor: 'pointer',
      display: 'block',
    }
  })

  const classes = useStyle()

  const displayData = (keyName, mapper = false) => {
    return data && keyName in data
      ? mapper
        ? mapper[data[keyName]]
        : data[keyName]
      : t('undefined')
  }

  const renderCourier = () => {
    const localLogoStyle = {
      maxHeight: 50,
      maxWidth: 150,
      marginRight: -10,
      marginBottom: -5,
    }
    const interLogoStyle = {
      maxHeight: 75,
      maxWidth: 150,
    }
    return courierLogoSrc ? (
      <img
        src={courierLogoSrc}
        style={data.delivery_type === 'inter' ? interLogoStyle : localLogoStyle}
        alt={data.courier_name ? data.courier_name : data.courier_code}
      />
    ) : (
      displayData('courier_code', couriersCodeToNameList)
    )
  }

  const renderTrackingUrl = () => {
    if (
      typeof window !== 'undefined' &&
      data['tracking_code'] &&
      data['delivery_type']
    ) {
      return `${window.location.hostname}/tracking?typeid=${data['delivery_type']}&tracking_code=${data['tracking_code']}`
    }
    return 'an error occured'
  }

  return (
    <>
      <Card className={classes.card}>
        <CardContent>
          <span className={classes.header}>{t('shipping-info')}</span>
          <Divider className={classes.divider} />
          <div className={classes.row}>
            <span className={classes.title}>{t('courier')}</span>
            <span className={classes.detail}>{renderCourier()}</span>
          </div>
          <div className={classes.row}>
            <span className={classes.title}>{t('status')}</span>
            <span
              className={classes.detail}
              // style={{ cursor: 'pointer' }}
              // onClick={() => setHelpModalOpen(true)}
            >
              {displayData(
                'order_status',
                language === 'th'
                  ? orderStatusToTextMap
                  : orderStatusToTextMapEng
              )}
            </span>
          </div>
          <div className={classes.row}>
            <span className={classes.title}>{t('info-tracking-no')}</span>
            <span className={classes.detail}>
              <span style={{ color: theme.palette.primary.main }}>
                {displayData('tracking_code')}
              </span>
            </span>
          </div>
          <div className={classes.row}>
            <span className={classes.title}>{t('tracking-no-courier')}</span>
            <span className={classes.detail}>
              {displayData('courier_tracking_code')}
            </span>
          </div>
          {'receiver' in data && data.receiver && (
            <div className={classes.row}>
              <span className={classes.title}>{t('receiver')}</span>
              <span className={classes.detail}>{displayData('receiver')}</span>
            </div>
          )}
          {'signature' in data && data.signature && (
            <div className={classes.signatureArea}>
              <img src={data.signature} className={classes.signature} />
              <MuiLink
                className={classes.seeSignature}
                onClick={() => setSignatureModalOpen(true)}
              >
                {t('see-large-signature')}
              </MuiLink>
              <SignatureModal
                isOpen={isSignatureModalOpen}
                setOpen={setSignatureModalOpen}
                src={data.signature}
              />
            </div>
          )}
          {!isMobile && (
            <>
              <span className={classes.title}>{t('copy-link')}</span>
              <div className={classes.fullWidth} style={{ marginTop: 8 }}>
                <CopyField
                  text={
                    data && 'tracking_code' in data ? renderTrackingUrl() : ''
                  }
                  copiedText={t('copied')}
                />
              </div>
            </>
          )}

        </CardContent>
        <div>
          <Link 
            href="https://www.tqm.co.th/car-insurance/online?utm_source=affiliate&utm_medium=mysave&utm_campaign=onlineshippop"
          >
            <a target="_blank">
              <img src="/static/img/shippop_x_tqm.png" alt="SHIPPOP x TQM" className={classes.bannerImage} />
            </a>
          </Link>
        </div>
      </Card>
      <TrackingHelpModal
        open={isHelpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />
      <input
        style={{ position: 'absolute', left: -999999 }}
        readOnly
        value={data && 'tracking_code' in data ? renderTrackingUrl() : ''}
        ref={mobileUrlRef}
      />
      {isMobile && (
        <>
          <ButtonSp
            fullWidth
            variant="outlined"
            style={{ marginTop: 20, height: 50 }}
            onClick={() => {
              if (typeof window !== 'undefined') {
                mobileUrlRef.current.select()
                document.execCommand('copy')
                setSnackbarOpen(true)
              }
            }}
          >
            <FileCopyOutlinedIcon fontSize="small" style={{ marginRight: 5 }} />
            {t('copy-tracking-link')}
          </ButtonSp>
          <CopiedSnackBar
            open={isSnackbarOpen}
            setOpen={setSnackbarOpen}
            text={t('copied')}
          />
        </>
      )}
    </>
  )
}

export default DetailCard
